"use client"

import { BlockchainsMoleculeReatom as BlockchainsMolecule } from "@/app/blockchains/blockchain-molecule.reatom"
import {
  useNativeCurrency,
  useWrappedCurrency,
} from "@/app/blockchains/use-blockchain"
import { NftSingleSell } from "@/app/collections/instant-sell/sell-dialog/nft-single-sell"
import { SellCollectionNftMoleculeReatom } from "@/app/collections/instant-sell/use-collection-nfts-sell.reatom"
import { PriceQuote } from "@/app/nft/price-quote/price-quote"
import { useSellState } from "@/app/nft/sell/use-sell-state"
import {
  useEvmServiceFee,
  useSolanaServiceFee,
} from "@/app/sdk/action-molecules/eclipse/fees-molecule"
import { useEvmAcceptCollectionBid } from "@/app/sdk/action-molecules/evm/evm-accept-collection-bid-molecule"
import { useSolanaAcceptCollectionBid } from "@/app/sdk/action-molecules/solana/solana-accept-collection-bid-molecule"
import type { Nft, NftMiniature } from "@anaconda/core/nft/types"
import type { PriceValue } from "@anaconda/core/price/types"
import { Trans } from "@lingui/react/macro"
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@rarible_int/anaconda_ui_components"
import { useMolecule } from "bunshi/react"
import { useEffect, useMemo, useRef } from "react"

interface Props {
  nft: Nft
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function NftSellDialog({ nft, open, onOpenChange }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[396px]">
        {open ? <NftSellDialogContent nft={nft} /> : null}
      </DialogContent>
    </Dialog>
  )
}

function NftSellDialogContent({ nft }: { nft: Nft }) {
  const blockchain = nft.collection.blockchain
  const { reatomIsEvmBlockchain, reatomIsSolanaBlockchain } =
    useMolecule(BlockchainsMolecule)

  // No reatomComponent here: accept hooks read/write offer atoms; wrapping
  // would re-subscribe on every setCollectionOffers and freeze the page.
  if (reatomIsEvmBlockchain()(blockchain)) {
    return <EvmNftSellDialogContent nft={nft} />
  }

  if (reatomIsSolanaBlockchain()(blockchain)) {
    return <SolanaNftSellDialogContent nft={nft} />
  }

  return null
}

function useNftSellOfferSync(nft: Nft) {
  const nftMiniature = useMemo<NftMiniature>(
    () => ({
      id: nft.id,
      collection: nft.collection,
      image: nft.image,
      name: nft.name,
      lastSellPrice: nft.lastSellPrice,
    }),
    [nft],
  )
  const nftsToSell = useMemo(() => [nftMiniature], [nftMiniature])

  const { sellPrice, offerByNft } = useSellState(nftsToSell)
  const { setCollectionOffers } = useMolecule(SellCollectionNftMoleculeReatom)
  const syncedOfferIdRef = useRef<string | undefined>(undefined)

  const matchedOffer = offerByNft[nft.id]?.offer
  const matchedOfferId = matchedOffer?.id

  // Sync once per offer id — avoid writing a new map object every render.
  useEffect(() => {
    if (!matchedOffer || !matchedOfferId) return
    if (syncedOfferIdRef.current === matchedOfferId) return

    syncedOfferIdRef.current = matchedOfferId
    setCollectionOffers(nft.collection.id, {
      isLoading: false,
      data: {
        offers: [matchedOffer],
      },
    })
  }, [matchedOffer, matchedOfferId, nft.collection.id, setCollectionOffers])

  const nativeCurrency = useNativeCurrency(nft.collection.blockchain)
  const wrappedCurrency = useWrappedCurrency(nft.collection.blockchain)

  const safeSellPrice = useMemo(
    () =>
      sellPrice ?? {
        amount: "0",
        currency: wrappedCurrency ?? nativeCurrency,
      },
    [sellPrice, wrappedCurrency, nativeCurrency],
  )

  return {
    nftMiniature,
    safeSellPrice,
    buttonDisabled: sellPrice === undefined,
  }
}

function NftSellDialogForm({
  nft,
  nftMiniature,
  safeSellPrice,
  serviceFeeQuery,
  buttonDisabled,
  mutate,
}: {
  nft: Nft
  nftMiniature: NftMiniature
  safeSellPrice: PriceValue
  serviceFeeQuery: {
    isLoading: boolean
    data: number | undefined
    error: Error | null
  }
  buttonDisabled: boolean
  mutate: () => void
}) {
  return (
    <form className="flex flex-col gap-y-2">
      <DialogHeader className="pb-2">
        <DialogTitle className="text-center text-lg">
          <Trans>Accept Offer</Trans>
        </DialogTitle>
      </DialogHeader>

      <NftSingleSell nft={nftMiniature} sellPrice={safeSellPrice} />

      <PriceQuote
        nftId={nft.id}
        price={safeSellPrice}
        serviceFeeQuery={serviceFeeQuery}
      />

      <DialogClose asChild>
        <Button
          className="flex-grow"
          type="button"
          onClick={() => {
            mutate()
          }}
          disabled={buttonDisabled}
        >
          <Trans>Accept Offer</Trans>
        </Button>
      </DialogClose>
    </form>
  )
}

function EvmNftSellDialogContent({ nft }: { nft: Nft }) {
  const { nftMiniature, safeSellPrice, buttonDisabled } =
    useNftSellOfferSync(nft)
  const { mutate } = useEvmAcceptCollectionBid()
  const serviceFeeQuery = useEvmServiceFee(
    nft.collection.blockchain,
    safeSellPrice,
  )

  return (
    <NftSellDialogForm
      nft={nft}
      nftMiniature={nftMiniature}
      safeSellPrice={safeSellPrice}
      serviceFeeQuery={serviceFeeQuery}
      buttonDisabled={buttonDisabled}
      mutate={mutate}
    />
  )
}

function SolanaNftSellDialogContent({ nft }: { nft: Nft }) {
  const { nftMiniature, safeSellPrice, buttonDisabled } =
    useNftSellOfferSync(nft)
  const { mutate } = useSolanaAcceptCollectionBid()
  const serviceFeeQuery = useSolanaServiceFee(
    nft.collection.blockchain,
    safeSellPrice,
  )

  return (
    <NftSellDialogForm
      nft={nft}
      nftMiniature={nftMiniature}
      safeSellPrice={safeSellPrice}
      serviceFeeQuery={serviceFeeQuery}
      buttonDisabled={buttonDisabled}
      mutate={mutate}
    />
  )
}
